import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetDripProgressions as GetDripProgressionsQuery,
    GetDripProgressionsVariables as GetDripProgressionsQueryVariables,
    GetDripProgressions_edition_queuedDripProgressions as QueuedDripProgressions,
} from 'nutshell-graphql-types';

import GET_DRIP_PROGRESSIONS from './graphql/queries/get-drip-progressions.graphql';

type Props = {
    editionId: string;
    children: (arg1: {
        dripProgressions: any[];
        error: string | null | undefined;
        isLoading: boolean;
        fetchMoreRows: () => Promise<any>;
        hasNextPage: boolean;
        refetch: () => Promise<any>;
    }) => React.ReactElement;
};

const LIMIT = 50;

export function DripProgressionsQuery(props: Props) {
    const {
        data,
        loading: isLoading,
        error,
        refetch,
        fetchMore,
    } = useQuery<GetDripProgressionsQuery, GetDripProgressionsQueryVariables>(
        GET_DRIP_PROGRESSIONS,
        {
            variables: {editionId: props.editionId, first: LIMIT},
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
        }
    );

    const edition = data ? data.edition : null;

    const queuedDripProgressions: QueuedDripProgressions | null | undefined =
        edition && edition.queuedDripProgressions ? edition.queuedDripProgressions : undefined;

    const fetchMoreRows = () => {
        return fetchMore({
            variables: {
                first: LIMIT,
                after: queuedDripProgressions ? queuedDripProgressions.pageInfo.endCursor : null,
            },
            // @ts-expect-error
            updateQuery: (
                prev: GetDripProgressionsQuery,
                newResults: {
                    fetchMoreResult: GetDripProgressionsQuery;
                }
            ) => {
                const oldDripProgressions =
                    prev.edition && prev.edition.queuedDripProgressions
                        ? prev.edition.queuedDripProgressions
                        : {edges: []};
                const fetchedDripProgressions =
                    newResults.fetchMoreResult.edition &&
                    newResults.fetchMoreResult.edition.queuedDripProgressions
                        ? newResults.fetchMoreResult.edition.queuedDripProgressions
                        : {edges: []};
                const pageInfo =
                    newResults.fetchMoreResult.edition &&
                    newResults.fetchMoreResult.edition.queuedDripProgressions &&
                    newResults.fetchMoreResult.edition.queuedDripProgressions.pageInfo
                        ? newResults.fetchMoreResult.edition.queuedDripProgressions.pageInfo
                        : undefined;
                // @ts-expect-error
                const edges = oldDripProgressions.edges.concat(fetchedDripProgressions.edges);

                const newEdition = {
                    ...newResults.fetchMoreResult.edition,
                    queuedDripProgressions: {
                        ...oldDripProgressions,
                        pageInfo: pageInfo,
                        edges: edges,
                    },
                } as const;

                return {edition: newEdition};
            },
        });
    };

    const dripProgressions = queuedDripProgressions
        ? queuedDripProgressions.edges.map((dripProgression) => {
              return dripProgression.node;
          })
        : [];

    const hasNextPage =
        edition && edition.queuedDripProgressions
            ? edition.queuedDripProgressions.pageInfo.hasNextPage
            : false;

    return props.children({
        dripProgressions,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading,
        refetch,
        fetchMoreRows,
        hasNextPage,
    });
}
